import type { TFunction } from "i18next";
import { EMAIL_ADDRESS_PATTERN } from "./emailValidation";

export type FieldValidationRules = {
    required?: boolean;
    maxLength?: number;
    minLength?: number;
    min?: number;
    max?: number;
    email?: boolean;
};

export type FieldValidationMessages = {
    required?: string;
    maxLength?: string;
    minLength?: string;
    min?: string;
    max?: string;
    number?: string;
    email?: string;
};

export type FieldValidationResult = {
    valid: boolean;
    message?: string;
};

const stringLength = (value: unknown): number => {
    if (value === null || value === undefined) {
        return 0;
    }
    return String(value).length;
};

const trimmedLength = (value: unknown): number => {
    if (value === null || value === undefined) {
        return 0;
    }
    return String(value).trim().length;
};

const toNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    const parsed = Number(String(value));
    return Number.isFinite(parsed) ? parsed : null;
};

export const buildFieldValidationMessages = (
    t: TFunction,
    rules?: FieldValidationRules,
): FieldValidationMessages | undefined => {
    if (!rules) {
        return undefined;
    }
    return {
        required: t("validation.required"),
        maxLength:
            rules.maxLength !== undefined
                ? t("validation.max-length", { count: rules.maxLength })
                : undefined,
        minLength:
            rules.minLength !== undefined
                ? t("validation.min-length", { count: rules.minLength })
                : undefined,
        min: rules.min !== undefined ? t("validation.min", { count: rules.min }) : undefined,
        max: rules.max !== undefined ? t("validation.max", { count: rules.max }) : undefined,
        number: t("validation.number"),
        email: rules.email ? t("invalid.email") : undefined,
    };
};

export type FieldValidationOptions = {
    /** When true, `value` is treated as a list (e.g. multi-select autocomplete). */
    multiple?: boolean;
};

const validateSingleFieldValue = (
    value: unknown,
    rules: FieldValidationRules,
    messages?: FieldValidationMessages,
): FieldValidationResult => {
    const text = value === null || value === undefined ? "" : String(value);
    const trimmed = text.trim();
    const hasText = trimmed.length > 0;

    if (rules.required && !hasText) {
        return { valid: false, message: messages?.required };
    }

    if (rules.minLength !== undefined && hasText && trimmed.length < rules.minLength) {
        return { valid: false, message: messages?.minLength };
    }

    if (rules.email && hasText && !EMAIL_ADDRESS_PATTERN.test(trimmed)) {
        return { valid: false, message: messages?.email };
    }

    if (rules.maxLength !== undefined && stringLength(value) > rules.maxLength) {
        return { valid: false, message: messages?.maxLength };
    }

    if (rules.min !== undefined || rules.max !== undefined) {
        const numericValue = toNumber(value);
        if (numericValue === null) {
            if (hasText || rules.required) {
                return { valid: false, message: messages?.number ?? messages?.required };
            }
            return { valid: true };
        }
        if (rules.min !== undefined && numericValue < rules.min) {
            return { valid: false, message: messages?.min };
        }
        if (rules.max !== undefined && numericValue > rules.max) {
            return { valid: false, message: messages?.max };
        }
    }

    return { valid: true };
};

export const validateFieldValue = (
    value: unknown,
    rules?: FieldValidationRules,
    messages?: FieldValidationMessages,
    options?: FieldValidationOptions,
): FieldValidationResult => {
    if (!rules) {
        return { valid: true };
    }

    if (options?.multiple || Array.isArray(value)) {
        const items = Array.isArray(value) ? value : [];
        const nonEmptyItems = items
            .map((item) => `${item ?? ""}`.trim())
            .filter((item) => item.length > 0);

        if (rules.required && nonEmptyItems.length === 0) {
            return { valid: false, message: messages?.required };
        }

        const itemRules: FieldValidationRules = { ...rules, required: false };
        for (const item of nonEmptyItems) {
            const itemResult = validateSingleFieldValue(item, itemRules, messages);
            if (!itemResult.valid) {
                return itemResult;
            }
        }
        return { valid: true };
    }

    return validateSingleFieldValue(value, rules, messages);
};

export const isFieldValueValid = (
    value: unknown,
    rules?: FieldValidationRules,
    messages?: FieldValidationMessages,
    options?: FieldValidationOptions,
): boolean => validateFieldValue(value, rules, messages, options).valid;

export const clampFieldText = (value: string, maxLength?: number): string =>
    maxLength !== undefined && value.length > maxLength ? value.slice(0, maxLength) : value;

export const validateFieldValues = (
    values: Record<string, unknown>,
    rulesByField: Record<string, FieldValidationRules | undefined>,
    messagesByField?: Record<string, FieldValidationMessages | undefined>,
): boolean =>
    Object.entries(rulesByField).every(([fieldName, rules]) =>
        isFieldValueValid(values[fieldName], rules, messagesByField?.[fieldName]),
    );
